import { useEffect, useMemo, useState } from 'react'

import complianceJson from '../../demo/fixtures/w2/compliance.json'
import { useRisksOf } from '../../demo/selectors'
import { useDemoStore } from '../../demo/store'
import type { Risk } from '../../demo/types'
import { Button, EmptyState, GlossaryText, Icon, Modal, Spinner, Toast, ToastHost } from '../../ui'
import {
  COMPLIANCE_COPY,
  duplicateTargetAd,
  licenseTargetCreative,
  riskDialogTitle,
} from '../monitor/compliance'
import { BannedWordEditor } from './BannedWordEditor'
import { CreativeSwapPicker } from './CreativeSwapPicker'
import styles from './ComplianceAlerts.module.css'

interface RiskDialogProps {
  open: boolean
  campaignId: string
  onClose: () => void
}

export function RiskDialog({ open, campaignId, onClose }: RiskDialogProps) {
  const risks = useRisksOf(campaignId)
  const ads = useDemoStore((state) => state.ads)
  const campaigns = useDemoStore((state) => state.campaigns)
  const creatives = useDemoStore((state) => state.creatives)
  const replaceAdCreative = useDemoStore((state) => state.replaceAdCreative)
  const resolveRisk = useDemoStore((state) => state.resolveRisk)
  const ignoreRisk = useDemoStore((state) => state.ignoreRisk)
  const renewLicense = useDemoStore((state) => state.renewLicense)
  const [editorFor, setEditorFor] = useState<string | null>(null)
  const [pickerFor, setPickerFor] = useState<string | null>(null)
  const [renewing, setRenewing] = useState<string | null>(null)
  const [processing, setProcessing] = useState<{ riskId: string; text: string } | null>(null)
  const [toast, setToast] = useState<string | null>(null)
  const [timers] = useState(() => new Set<number>())

  useEffect(() => () => {
    timers.forEach((timer) => window.clearTimeout(timer))
    timers.clear()
  }, [timers])

  useEffect(() => {
    if (!open || risks.length > 0) return
    const timer = window.setTimeout(onClose, COMPLIANCE_COPY.autoCloseMs)
    return () => window.clearTimeout(timer)
  }, [onClose, open, risks.length])

  const duplicateTarget = useMemo(() => duplicateTargetAd(ads, campaignId), [ads, campaignId])
  const campaignName = campaigns.find((campaign) => campaign.id === campaignId)?.adName ?? campaignId

  const ignore = (risk: Risk) => {
    if (window.confirm(complianceJson.actions.ignore.confirm)) ignoreRisk(risk.id)
  }

  const ignoreAll = () => {
    if (!window.confirm(complianceJson.actions.ignore.confirm)) return
    risks.forEach((risk) => ignoreRisk(risk.id))
  }

  const renew = (risk: Risk) => {
    const creative = licenseTargetCreative(ads, creatives, campaignId, risk.text)
    if (!creative || renewing) return
    setRenewing(risk.id)
    setProcessing({ riskId: risk.id, text: `正在核对「${creative.name}」的授权期限` })
    const timer = window.setTimeout(() => {
      timers.delete(timer)
      renewLicense(creative.id)
      const renewed = useDemoStore.getState().creatives.find((item) => item.id === creative.id)
      setRenewing(null)
      setProcessing(null)
      setToast(`已为「${creative.name}」续期至 ${renewed?.licenseUntil ?? ''}`)
    }, complianceJson.actions.renew.ms)
    timers.add(timer)
  }

  const handleRisk = (risk: Risk) => {
    if (risk.type === 'banned_word') setEditorFor(risk.id)
    else if (risk.type === 'duplicate') setPickerFor(risk.id)
    else renew(risk)
  }

  const editorRisk = risks.find((risk) => risk.id === editorFor)
  const pickerRisk = risks.find((risk) => risk.id === pickerFor)

  return (
    <>
      <Modal
        open={open}
        title={<span className={styles.dialogTitle}>{riskDialogTitle(risks.length)}</span>}
        onClose={onClose}
        footer={risks.length > 0 ? (
          <>
            <Button variant="secondary" onClick={ignoreAll}>全部忽略</Button>
            <Button onClick={onClose}>知道了</Button>
          </>
        ) : undefined}
      >
        <div data-scope="state-dialog-risk" className={styles.dialogContent}>
          {risks.length === 0 ? (
            <EmptyState className={styles.compactEmpty} icon="check-circle" title={COMPLIANCE_COPY.riskEmpty} text="风险计数与提示已同步更新" />
          ) : (
            <>
              <div className={styles.riskList}>
                {risks.map((risk) => (
                  <div className={`${styles.riskItem} ${processing?.riskId === risk.id ? styles.riskItemProcessing : ''}`} key={risk.id}>
                    <Icon name="alert" size={17} strokeWidth={2} />
                    <div className={styles.riskText}>
                      <p><GlossaryText text={risk.text} /></p>
                      <span>建议：<GlossaryText text={risk.action} /></span>
                    </div>
                    <Button className={styles.riskAction} variant="secondary" disabled={processing !== null} onClick={() => handleRisk(risk)}>
                      {renewing === risk.id ? <><Spinner />续期中</> : '立即处理'}
                    </Button>
                    <Button className={styles.ignoreAction} variant="link" onClick={() => ignore(risk)}>{complianceJson.actions.ignore.label}</Button>
                    {processing?.riskId === risk.id ? <div className={styles.riskProcess} data-scope="state-risk-processing"><Spinner />{processing.text}</div> : null}
                  </div>
                ))}
              </div>
              {editorRisk ? (
                <BannedWordEditor onCancel={() => setEditorFor(null)} onSave={() => {
                  setEditorFor(null)
                  setProcessing({ riskId: editorRisk.id, text: `正在检查修改后的文案，高危词「${COMPLIANCE_COPY.bannedWord}」已移除` })
                  const timer = window.setTimeout(() => {
                    timers.delete(timer)
                    resolveRisk(editorRisk.id)
                    setProcessing(null)
                    setToast(`已改掉高危词「${COMPLIANCE_COPY.bannedWord}」，这条拒登风险已消`)
                  }, COMPLIANCE_COPY.autoCloseMs)
                  timers.add(timer)
                }} />
              ) : null}
              {pickerRisk && duplicateTarget ? (
                <CreativeSwapPicker
                  campaignId={campaignId}
                  currentCreativeId={duplicateTarget.creativeId}
                  unusedOnly
                  onCancel={() => setPickerFor(null)}
                  onSelect={(creativeId) => {
                    const creative = creatives.find((item) => item.id === creativeId)
                    setPickerFor(null)
                    if (!creative) return
                    setProcessing({ riskId: pickerRisk.id, text: `正在把「${duplicateTarget.name}」换成「${creative.name}」` })
                    const timer = window.setTimeout(() => {
                      timers.delete(timer)
                      replaceAdCreative(duplicateTarget.id, creativeId)
                      resolveRisk(pickerRisk.id)
                      setProcessing(null)
                      setToast(`已把「${campaignName}」的素材换成「${creative.name}」，重复度风险已消`)
                    }, complianceJson.actions.replaceCreative.reviewMs)
                    timers.add(timer)
                  }}
                />
              ) : null}
            </>
          )}
        </div>
      </Modal>
      {toast ? <ToastHost><Toast onDismiss={() => setToast(null)}>{toast}</Toast></ToastHost> : null}
    </>
  )
}
